import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea, ReferenceLine } from 'recharts';
import api from '../services/api';
import { getChartTimeFormat, formatTooltipTime, adjustDateForTimezone, getCurrentLocalDateString, debugDateConversion } from '../services/dateUtils';

const DeviceHistory = ({ deviceId, user, onClose, viewPreferences = {}, standalone = false }) => {
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [availableMagnitudes, setAvailableMagnitudes] = useState([]);
  const [selectedMagnitudes, setSelectedMagnitudes] = useState([]);
  const [lastDeviceId, setLastDeviceId] = useState(null);
  const [viewMode, setViewMode] = useState('separate');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 2);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [groupBy, setGroupBy] = useState('raw'); // Mostrar todos los datos por defecto
  const [dateRangeDetected, setDateRangeDetected] = useState(false);
  
  // Estados para el zoom
  const [refAreaLeft, setRefAreaLeft] = useState('');
  const [refAreaRight, setRefAreaRight] = useState('');
  const [left, setLeft] = useState('dataMin');
  const [right, setRight] = useState('dataMax');
  const [data, setData] = useState([]);
  const [commandLogs, setCommandLogs] = useState([]);
  
  // Colores para diferentes magnitudes (16 colores para soportar muchas magnitudes)
  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16',
    '#f97316', '#14b8a6', '#a855f7', '#0ea5e9', '#f43f5e', '#22c55e', '#eab308', '#6366f1'
  ];

  // Obtener información del dispositivo y sus magnitudes
  const fetchDeviceInfo = async () => {
    try {
      const response = await api.get(`/api/devices/${deviceId}`);
      setDeviceInfo(response.data);
      console.log('Device info:', response.data);
      console.log(`📋 Device has ${response.data.magnitudes?.length || 0} magnitudes configured`);
      console.log(`📋 Magnitudes names:`, response.data.magnitudes?.map(m => m.name));
      console.log(`👤 User role: ${user?.role}, isAdmin: ${user?.role === 'admin'}`);

      if (response.data.magnitudes && response.data.magnitudes.length > 0) {
        // NO filtrar duplicados - mostrar todas las magnitudes configuradas
        // El usuario es responsable de configurar nombres únicos
        // Filtrar primero por showInDashboard
        const uniqueMagnitudes = response.data.magnitudes.filter(mag => mag.showInDashboard !== false);

        console.log(`🔧 DeviceHistory: Device has ${response.data.magnitudes.length} magnitudes configured, ${uniqueMagnitudes.length} visible`);
        console.log('📋 All magnitudes:', uniqueMagnitudes.map(m => ({ name: m.name, label: m.label, jsonPath: m.jsonPath })));

        // CREAR COPIA PROFUNDA de las magnitudes para evitar referencias compartidas
        let magnitudesCopy = uniqueMagnitudes.map(mag => ({
          ...mag,
          // Asegurar que cada propiedad sea independiente
          name: mag.name,
          label: mag.label,
          jsonPath: mag.jsonPath,
          unit: mag.unit,
          decimals: mag.decimals,
          valueType: mag.valueType,
          minNormalValue: mag.minNormalValue,
          maxNormalValue: mag.maxNormalValue
        }));

        // FILTRAR POR PERMISOS DE USUARIO
        if (user && user.role !== 'admin') {
          // Para usuarios no admin, filtrar según devicePermissions
          if (user.devicePermissions && user.devicePermissions[deviceId]) {
            const devicePermissions = user.devicePermissions[deviceId];
            if (devicePermissions.magnitudes) {
              magnitudesCopy = magnitudesCopy.filter(magnitude => {
                return devicePermissions.magnitudes.includes(magnitude.name);
              });
              console.log(`🔒 DeviceHistory: Filtered to ${magnitudesCopy.length} enabled magnitudes for user ${user.username}`);
            } else {
              magnitudesCopy = [];
              console.log(`🔒 DeviceHistory: No magnitudes permissions for device ${deviceId}`);
            }
          } else {
            // Si no hay permisos para este dispositivo, no mostrar ninguna
            magnitudesCopy = [];
            console.log(`🔒 DeviceHistory: No device permissions configured for user ${user.username}`);
          }
        }

        // FILTRAR POR PREFERENCIAS DE VISUALIZACIÓN DEL USUARIO (aplica a todos, incluido admin)
        const userViewPrefs = viewPreferences[deviceId];
        if (userViewPrefs !== undefined && Array.isArray(userViewPrefs)) {
          if (userViewPrefs.length === 0) {
            // Si el array está vacío, no mostrar ninguna magnitud
            magnitudesCopy = [];
            console.log(`👁️ DeviceHistory: User has no magnitudes selected for device ${deviceId}`);
          } else {
            // Filtrar solo las magnitudes que el usuario quiere ver
            magnitudesCopy = magnitudesCopy.filter(magnitude =>
              userViewPrefs.includes(magnitude.name)
            );
            console.log(`👁️ DeviceHistory: Filtered to ${magnitudesCopy.length} magnitudes by user view preferences`);
          }
        }

        setAvailableMagnitudes(magnitudesCopy);
        
        // LOGGING ESPECÍFICO PARA DEBUG MAGNITUDES BOOLEANAS
        console.log(`📊 DeviceHistory: Available magnitudes for ${deviceId}:`, magnitudesCopy.map(m => ({
          name: m.name,
          label: m.label,
          valueType: m.valueType,
          currentValue: m.currentValue,
          jsonPath: m.jsonPath
        })));
        
        const booleanMags = magnitudesCopy.filter(m => m.valueType === 'boolean');
        if (booleanMags.length > 0) {
          console.log(`🔘 DeviceHistory: Found ${booleanMags.length} boolean magnitudes:`, booleanMags.map(m => m.label));
        }
        
        // Al abrir un dispositivo nuevo, arrancar SIN magnitudes seleccionadas (el usuario elige cuáles ver)
        if (lastDeviceId !== deviceId) {
          setSelectedMagnitudes([]);
          setLastDeviceId(deviceId);
        }
      } else {
        const defaultMagnitude = {
          name: 'value',
          label: response.data.name || 'Valor',
          jsonPath: 'value',
          unit: response.data.unit || '',
          decimals: response.data.decimals || 1,
          minNormalValue: response.data.minNormalValue,
          maxNormalValue: response.data.maxNormalValue
        };
        setAvailableMagnitudes([defaultMagnitude]);
        // Arrancar sin selección también en el caso de magnitud única
        if (lastDeviceId !== deviceId) {
          setSelectedMagnitudes([]);
          setLastDeviceId(deviceId);
        }
      }
    } catch (error) {
      console.error('Error fetching device info:', error);
    }
  };

  // Auto-detectar rango de fechas
  const autoDetectDateRange = async () => {
    try {
      const cacheBuster = `?t=${Date.now()}`;
      const response = await api.get(`/api/devices/${deviceId}/date-range${cacheBuster}`);
      if (response.data.firstDate && response.data.lastDate) {
        const firstDate = new Date(response.data.firstDate);
        const lastDate = new Date(response.data.lastDate);
        
        setStartDate(firstDate.toISOString().split('T')[0]);
        setEndDate(lastDate.toISOString().split('T')[0]);
        setDateRangeDetected(true);
        
        console.log('Auto-detected date range:', {
          first: firstDate.toISOString(),
          last: lastDate.toISOString(),
          total: response.data.totalRecords
        });
      }
    } catch (error) {
      console.log('Using default date range');
    }
  };

  // Efecto para resetear estado cuando cambie el dispositivo
  useEffect(() => {
    if (deviceId && deviceId !== lastDeviceId) {
      console.log('🔄 DeviceHistory: Cambiando dispositivo de', lastDeviceId, 'a', deviceId);
      // Resetear estados cuando cambie el dispositivo
      setSelectedMagnitudes([]);
      setAvailableMagnitudes([]);
      setHistoryData(null);
      setDeviceInfo(null);
    }
  }, [deviceId, lastDeviceId]);

  useEffect(() => {
    if (deviceId) {
      fetchDeviceInfo();
      autoDetectDateRange();
    }
  }, [deviceId]);

  useEffect(() => {
    if (deviceId && deviceInfo && selectedMagnitudes.length > 0) {
      fetchHistory();
      fetchCommandLogs();
    }
  }, [deviceId, deviceInfo, selectedMagnitudes]);

  // Refrescar command logs cuando cambien las fechas
  useEffect(() => {
    if (deviceId && startDate && endDate && deviceInfo && selectedMagnitudes.length > 0) {
      console.log('📅 Date range changed, reloading data...');
      fetchHistory();
      fetchCommandLogs();
    }
  }, [startDate, endDate]);

  // Auto-refresh cada 30 segundos si está habilitado
  useEffect(() => {
    if (!autoRefresh || !deviceId || selectedMagnitudes.length === 0) return;
    
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing history data...');
      fetchHistory();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, deviceId, selectedMagnitudes, groupBy, startDate, endDate]);

  const fetchHistory = async (groupByParam = null) => {
    if (selectedMagnitudes.length === 0) return;
    
    setLoading(true);
    const actualGroupBy = groupByParam || groupBy;
    console.log('=== FETCHING HISTORY ===');
    console.log('Selected Magnitudes:', selectedMagnitudes.map(m => m.label));
    console.log('GroupBy:', actualGroupBy);
    console.log('Date Range:', startDate, 'to', endDate);
    
    // Verificar que las fechas estén en orden correcto
    if (new Date(startDate) > new Date(endDate)) {
      console.error('❌ Start date is after end date, swapping them');
      const temp = startDate;
      setStartDate(endDate);
      setEndDate(temp);
      return;
    }
    
    try {
      // Usar función de debug para verificar conversiones
      const adjustedStartDate = debugDateConversion(startDate, true);
      const adjustedEndDate = debugDateConversion(endDate, false);
      
      const cacheBuster = `&t=${Date.now()}`;
      // Usar API sin agregación para obtener todos los datos
      const url = `/api/devices/${deviceId}/history?startDate=${adjustedStartDate}&endDate=${adjustedEndDate}&groupBy=${actualGroupBy}&aggregate=false&interval=raw${cacheBuster}`;
      console.log('🔗 URL with timezone aggregation:', url);
      
      const response = await api.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('Response received:');
      console.log('- Full response:', response.data);
      console.log('- Response keys:', Object.keys(response.data));
      console.log('- Total records:', response.data.stats?.totalRecords);
      console.log('- History length:', response.data.history?.length);
      console.log('- History sample:', response.data.history?.[0]);
      console.log('- Timestamp:', response.data.stats?.timestamp);
      
      setHistoryData(response.data);
      setLastUpdateTime(new Date());
      
      // Determinar si la respuesta es un array directo o un objeto con propiedades
      let historyArray = null;
      if (Array.isArray(response.data)) {
        // Si es un array directo, usarlo directamente
        historyArray = response.data;
        console.log('✅ Using direct array response with', historyArray.length, 'records');
      } else if (response.data?.history || response.data?.chartData) {
        // Si es un objeto con propiedades, usar history o chartData
        historyArray = response.data.history || response.data.chartData;
        console.log('✅ Using object.history response with', historyArray?.length, 'records');
      }
      
      if (historyArray && historyArray.length > 0) {
        const formattedData = formatChartDataForAllMagnitudes(
          historyArray, 
          selectedMagnitudes,
          actualGroupBy
        );
        setData(formattedData);
        setLeft('dataMin');
        setRight('dataMax');
        setRefAreaLeft('');
        setRefAreaRight('');
        console.log('📊 Chart data formatted:', formattedData.length, 'points ready for display');
      } else {
        console.log('❌ No history data found in response');
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
    setLoading(false);
  };

  // Obtener logs de comandos ejecutados
  const fetchCommandLogs = async () => {
    try {
      console.log('=== FETCHING COMMAND LOGS ===');
      console.log('Device ID:', deviceId);
      console.log('Date Range:', startDate, 'to', endDate);
      
      const params = {
        startDate: startDate,
        endDate: endDate + 'T23:59:59.999Z' // Include the full end date
      };
      
      const response = await api.getCommandLogs(deviceId, params);
      console.log('📋 Command logs response:', response);
      console.log('📋 Response data:', response.data);
      console.log('📋 Response status:', response.status);
      if (response.data && response.data.success) {
        console.log(`📝 Retrieved ${response.data.count} command logs`);
        console.log('Command logs data:', response.data.data);
        setCommandLogs(response.data.data || []);
      } else {
        console.log('❌ No command logs found');
        setCommandLogs([]);
      }
    } catch (error) {
      console.error('❌ Error fetching command logs:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      setCommandLogs([]);
    }
  };

  // Función auxiliar para obtener valor nested
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return null;
    return path.split('.').reduce((current, key) => {
      return (current !== null && current !== undefined && current[key] !== undefined) ? current[key] : null;
    }, obj);
  };

  const formatChartDataForAllMagnitudes = (historyData, magnitudes, groupByMode) => {
    if (!historyData || !magnitudes || magnitudes.length === 0) return [];
    
    console.log(`Formatting data for ${magnitudes.length} magnitudes, Points: ${historyData.length}`);
    
    let formattedData = [];
    
    // Generar función de formateo usando utilidad
    const timeFormatter = getChartTimeFormat(historyData, groupByMode);
    
    historyData.forEach((item, index) => {
      let dataPoint = {
        name: index,
        time: timeFormatter(index),
        fullTime: formatTooltipTime(item.timestamp),
        timestamp: item.timestamp
      };
      
      magnitudes.forEach((magnitude) => {
        let value = null;
        
        if (item.data && typeof item.data === 'object') {
          value = getNestedValue(item.data, magnitude.jsonPath);
        }
        
        if (value === null || value === undefined) {
          if (magnitude.jsonPath === 'temperature') {
            value = item.data?.temperature || item.temperature || null;
          } else if (magnitude.jsonPath === 'humidity') {
            value = item.data?.humidity || item.humidity || null;
          } else if (magnitude.jsonPath === 'value') {
            value = item.value || item.data?.value || null;
          } else {
            value = item[magnitude.jsonPath] || null;
          }
        }
        
        if (value !== null && value !== undefined) {
          let numericValue;
          
          // Convertir valores según el tipo de magnitud
          if (magnitude.valueType === 'boolean') {
            // Normalizar valores booleanos a 0/10 para gráficos (mejor visualización)
            if (typeof value === 'boolean') {
              numericValue = value ? 10 : 0;
            } else if (typeof value === 'string') {
              const lowerValue = value.toLowerCase();
              if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'on' || lowerValue === 'yes') {
                numericValue = 10;
              } else if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'off' || lowerValue === 'no') {
                numericValue = 0;
              } else {
                numericValue = null; // Valor inválido
              }
            } else {
              numericValue = value ? 10 : 0; // Fallback
            }
            
            console.log(`📊 Boolean magnitude ${magnitude.label}: "${value}" → ${numericValue}`);
          } else {
            // Para magnitudes numéricas, usar parseFloat como antes
            const decimals = magnitude.decimals || 1;
            if (typeof value === 'number') {
              numericValue = parseFloat(value.toFixed(decimals));
            } else {
              numericValue = parseFloat(value);
              if (!isNaN(numericValue)) {
                numericValue = parseFloat(numericValue.toFixed(decimals));
              } else {
                numericValue = null;
              }
            }
          }
          
          if (numericValue !== null) {
            dataPoint[magnitude.label] = numericValue;
          }
        }
      });
      
      const hasValues = magnitudes.some(mag => dataPoint[mag.label] !== undefined);
      if (hasValues) {
        formattedData.push(dataPoint);
      }
    });
    
    console.log(`Formatted ${formattedData.length} points`);
    return formattedData;
  };

  // Helper para crear un ID único para cada magnitud (usando label + jsonPath)
  const getMagnitudeId = (mag) => `${mag.label}_${mag.jsonPath}`;

  const handleMagnitudeToggle = (magnitude) => {
    const magId = getMagnitudeId(magnitude);
    console.log(`📈 DeviceHistory [${deviceId}]: Toggleando magnitud`, magnitude.label, magId);
    setSelectedMagnitudes(prev => {
      // CREAR COPIA PROFUNDA para evitar mutación del estado
      const currentMagnitudes = prev.map(m => ({ ...m }));
      const isSelected = currentMagnitudes.some(m => getMagnitudeId(m) === magId);
      console.log(`📊 Magnitudes actuales:`, currentMagnitudes.map(m => m.label));

      let newMagnitudes;
      if (isSelected) {
        if (currentMagnitudes.length === 1) {
          console.log('⚠️ No se puede deseleccionar la última magnitud');
          return currentMagnitudes; // No permitir deseleccionar la última
        }
        newMagnitudes = currentMagnitudes.filter(m => getMagnitudeId(m) !== magId);
      } else {
        // Crear copia profunda de la magnitud a agregar
        const newMagnitude = { ...magnitude };
        newMagnitudes = [...currentMagnitudes, newMagnitude];
      }

      console.log(`📈 Nuevas magnitudes:`, newMagnitudes.map(m => m.label));
      return newMagnitudes;
    });
  };

  const handleGroupByChange = async (e) => {
    const newValue = e.target.value;
    console.log('>>> GROUP BY CHANGED TO:', newValue);
    setGroupBy(newValue);
    await fetchHistory(newValue);
  };

  const handleDateChange = () => {
    console.log('📅 Date range changed, fetching new data...');
    console.log('Start Date:', startDate);
    console.log('End Date:', endDate);
    setData([]);
    fetchHistory();
  };

  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    fetchHistory();
  };

  const zoom = () => {
    if (refAreaLeft === refAreaRight || refAreaLeft === '') {
      setRefAreaLeft('');
      setRefAreaRight('');
      return;
    }

    let leftPoint = refAreaLeft;
    let rightPoint = refAreaRight;
    if (refAreaLeft > refAreaRight) {
      leftPoint = refAreaRight;
      rightPoint = refAreaLeft;
    }

    setLeft(leftPoint);
    setRight(rightPoint);
    setRefAreaLeft('');
    setRefAreaRight('');
  };

  const zoomOut = () => {
    setLeft('dataMin');
    setRight('dataMax');
    setRefAreaLeft('');
    setRefAreaRight('');
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Cargando historial...</div>
      </div>
    );
  }

  const isZoomed = left !== 'dataMin' || right !== 'dataMax';

  const calculateStats = () => {
    if (!data || data.length === 0 || selectedMagnitudes.length === 0) return {};
    
    const stats = {};
    selectedMagnitudes.forEach(magnitude => {
      const values = data.map(d => d[magnitude.label]).filter(v => v !== undefined && v !== null);
      if (values.length > 0) {
        stats[magnitude.label] = {
          avg: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(magnitude.decimals || 1),
          min: Math.min(...values).toFixed(magnitude.decimals || 1),
          max: Math.max(...values).toFixed(magnitude.decimals || 1),
          count: values.length,
          unit: magnitude.unit || ''
        };
      }
    });
    return stats;
  };

  const stats = calculateStats();

  const exportToCSV = () => {
    if (!data || data.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Combinar datos de historial y comandos ejecutados, ordenados por timestamp
    const allData = [];

    // Agregar datos del historial
    data.forEach(item => {
      allData.push({
        timestamp: new Date(item.timestamp),
        type: 'data',
        data: item
      });
    });

    // Agregar comandos ejecutados
    if (commandLogs && commandLogs.length > 0) {
      console.log('🔍 Processing command logs for CSV:', commandLogs);
      commandLogs.forEach((log, index) => {
        console.log(`📝 Command log ${index}:`, log);
        allData.push({
          timestamp: new Date(log.executedAt),
          type: 'command',
          data: log
        });
      });
    }

    // Ordenar todo por timestamp
    allData.sort((a, b) => a.timestamp - b.timestamp);

    // Crear headers
    const headers = ['Dispositivo', 'Dia', 'Hora', 'Tipo'];
    selectedMagnitudes.forEach(magnitude => {
      headers.push(magnitude.label + (magnitude.unit ? ` (${magnitude.unit})` : ''));
    });
    headers.push('Comando', 'Estado/Accion', 'Usuario', 'Payload Completo');

    // Crear filas de datos
    const rows = allData.map(item => {
      const row = [
        deviceInfo?.name || `Dispositivo ${deviceId}`,
        item.timestamp.toLocaleDateString('es-AR'),
        item.timestamp.toLocaleTimeString('es-AR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }),
        item.type === 'data' ? 'Datos' : 'Comando'
      ];

      if (item.type === 'data') {
        // Agregar valores de magnitudes
        selectedMagnitudes.forEach(magnitude => {
          const value = item.data[magnitude.label];
          if (value !== undefined && value !== null) {
            // Formatear decimales con coma como separador decimal
            const formattedValue = typeof value === 'number' ? value.toString().replace('.', ',') : value;
            row.push(formattedValue);
          } else {
            row.push('');
          }
        });
        // Campos de comando vacíos para datos
        row.push('', '', '', '');
      } else {
        // Para comandos, dejar magnitudes vacías
        selectedMagnitudes.forEach(() => {
          row.push('');
        });

        // Procesar información del comando
        console.log('🎯 Processing command data:', item.data);
        const commandName = item.data.commandLabel || item.data.commandId || '';
        const payload = item.data.payload;
        const username = item.data.executedBy?.username || '';

        console.log('📊 Command details:', {
          commandName,
          payload: payload,
          username,
          executedBy: item.data.executedBy,
          allFields: Object.keys(item.data)
        });

        let commandState = '';
        let payloadStr = '';

        // Analizar el payload para determinar el estado
        console.log('🔍 Payload type:', typeof payload, 'Value:', payload);

        let parsedPayload = payload;

        // Si es string, intentar parsearlo
        if (typeof payload === 'string') {
          try {
            parsedPayload = JSON.parse(payload);
            payloadStr = payload;
            console.log('✅ Payload parsed successfully:', parsedPayload);
          } catch (e) {
            console.log('❌ Failed to parse payload as JSON:', e.message);
            payloadStr = payload;
            parsedPayload = null;
          }
        } else if (typeof payload === 'object' && payload !== null) {
          payloadStr = JSON.stringify(payload);
          parsedPayload = payload;
          console.log('📦 Payload is already object:', parsedPayload);
        } else {
          payloadStr = String(payload || '');
          parsedPayload = null;
          console.log('⚠️ Payload is primitive value:', payload);
        }

        // Determinar estado basado en el payload parseado
        if (parsedPayload && typeof parsedPayload === 'object') {
          if (parsedPayload.action) {
            commandState = `Com${parsedPayload.action.toUpperCase()}`;
          } else if (parsedPayload.value !== undefined) {
            if (typeof parsedPayload.value === 'boolean') {
              commandState = parsedPayload.value ? 'ComON' : 'ComOFF';
            } else {
              commandState = `ComVAL(${parsedPayload.value})`;
            }
          } else if (parsedPayload.state) {
            commandState = `Com${parsedPayload.state.toUpperCase()}`;
          } else {
            commandState = 'ComEXEC';
          }
        } else {
          commandState = 'ComEXEC';
        }

        console.log('🎯 Final command state:', commandState);

        // Agregar información del comando
        row.push(commandName);
        row.push(commandState);
        row.push(item.data.executedBy?.username || '');
        row.push(payloadStr);
      }

      return row;
    });

    // Convertir a CSV
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historial_completo_${deviceInfo?.name || deviceId}_${startDate}_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función helper para obtener las posiciones X de los comandos en el gráfico
  const getCommandMarkersForChart = () => {
    console.log('🎯 Getting command markers...');
    console.log('Command logs:', commandLogs);
    console.log('Chart data length:', data ? data.length : 0);
    
    if (!commandLogs || commandLogs.length === 0 || !data || data.length === 0) {
      console.log('❌ No command logs or chart data available');
      return [];
    }
    
    const markers = commandLogs.map(log => {
      const logTime = new Date(log.executedAt);
      console.log(`🕐 Processing command log: ${log.commandLabel} at ${log.executedAt}`);
      
      // Buscar el punto más cercano en tiempo y usar su valor "name" como X
      let closestPoint = null;
      let minDiff = Infinity;
      
      data.forEach((point, index) => {
        const pointTime = new Date(point.timestamp);
        const diff = Math.abs(pointTime.getTime() - logTime.getTime());
        if (diff < minDiff) {
          minDiff = diff;
          closestPoint = point;
        }
      });
      
      console.log(`📍 Closest point for command: name=${closestPoint?.name}, timestamp=${closestPoint?.timestamp}`);
      
      return {
        ...log,
        xValue: closestPoint ? closestPoint.name : 0  // Usar el valor "name" que es lo que usa el eje X
      };
    });
    
    console.log('✅ Generated command markers:', markers);
    return markers;
  };

  const renderCombinedChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart 
        data={data}
        onMouseDown={(e) => {
          if (e && e.activeTooltipIndex !== undefined) {
            setRefAreaLeft(e.activeTooltipIndex);
          }
        }}
        onMouseMove={(e) => {
          if (refAreaLeft !== '' && e && e.activeTooltipIndex !== undefined) {
            setRefAreaRight(e.activeTooltipIndex);
          }
        }}
        onMouseUp={zoom}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          allowDataOverflow
          dataKey="name"
          domain={[left, right]}
          type="number"
          tickFormatter={(value) => {
            if (data[value]) {
              return data[value].time || '';
            }
            return '';
          }}
          angle={-45}
          textAnchor="end"
          height={80}
          tick={{ fontSize: 10 }}
          interval="preserveStartEnd"
        />
        <YAxis
          domain={['auto', 'auto']}
          type="number"
          tick={{ fontSize: 12 }}
        />
        <Tooltip 
          content={({ active, payload }) => {
            if (active && payload && payload[0]) {
              return (
                <div style={{
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '0.5rem',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>
                    {payload[0].payload.fullTime}
                  </p>
                  {payload.map((entry, index) => {
                    const magnitude = Array.isArray(selectedMagnitudes) ? selectedMagnitudes.find(m => m.label === entry.dataKey) : null;
                    let displayValue;
                    
                    if (magnitude?.valueType === 'boolean') {
                      // Para magnitudes booleanas, mostrar ON/OFF
                      displayValue = entry.value === 10 ? 'ON' : 'OFF';
                    } else {
                      // Para magnitudes numéricas, mostrar con decimales y unidad
                      displayValue = `${entry.value?.toFixed(magnitude?.decimals || 1)}${magnitude?.unit || ''}`;
                    }
                    
                    return (
                      <p key={index} style={{ margin: 0, color: entry.color }}>
                        {entry.dataKey}: {displayValue}
                      </p>
                    );
                  })}
                </div>
              );
            }
            return null;
          }}
        />
        <Legend />
        
        {selectedMagnitudes.map((magnitude, index) => (
          <Line 
            key={magnitude.name}
            type="monotone" 
            dataKey={magnitude.label}
            stroke={colors[index % colors.length]}
            strokeWidth={2}
            dot={false}
            name={`${magnitude.label}${magnitude.unit ? ` (${magnitude.unit})` : ''}`}
            animationDuration={300}
            connectNulls={true}
          />
        ))}

        {/* Marcas verticales de comandos ejecutados */}
        {getCommandMarkersForChart().map((commandMarker, index) => (
          <ReferenceLine 
            key={`command-${index}`}
            x={commandMarker.xValue}
            stroke="#ef4444"
            strokeWidth={2}
            strokeDasharray="4 4"
            label={{ 
              value: `${commandMarker.commandIcon} ${commandMarker.commandLabel}`,
              position: 'top',
              offset: 10,
              style: { 
                fontSize: '10px', 
                fill: '#ef4444', 
                fontWeight: 'bold',
                background: 'white',
                padding: '2px 4px',
                borderRadius: '3px',
                border: '1px solid #ef4444'
              }
            }}
          />
        ))}
        
        {refAreaLeft && refAreaRight ? (
          <ReferenceArea 
            x1={refAreaLeft} 
            x2={refAreaRight} 
            strokeOpacity={0.3}
            fill="#3b82f6"
            fillOpacity={0.3}
          />
        ) : null}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderSeparateCharts = () => (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {selectedMagnitudes.map((magnitude, index) => (
        <div key={magnitude.name} style={{ 
          background: '#f9fafb', 
          padding: '1rem', 
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: colors[index % colors.length] }}>
            {magnitude.label} {magnitude.unit && `(${magnitude.unit})`}
          </h4>
          
          {stats[magnitude.label] && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <div style={{ textAlign: 'center', background: 'white', padding: '0.5rem', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Promedio</div>
                <div style={{ fontWeight: 'bold' }}>{stats[magnitude.label].avg}{magnitude.unit}</div>
              </div>
              <div style={{ textAlign: 'center', background: 'white', padding: '0.5rem', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Mínimo</div>
                <div style={{ fontWeight: 'bold' }}>{stats[magnitude.label].min}{magnitude.unit}</div>
              </div>
              <div style={{ textAlign: 'center', background: 'white', padding: '0.5rem', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Máximo</div>
                <div style={{ fontWeight: 'bold' }}>{stats[magnitude.label].max}{magnitude.unit}</div>
              </div>
              <div style={{ textAlign: 'center', background: 'white', padding: '0.5rem', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Registros</div>
                <div style={{ fontWeight: 'bold' }}>{stats[magnitude.label].count}</div>
              </div>
            </div>
          )}
          
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={data}
              onMouseDown={(e) => {
                if (e && e.activeTooltipIndex !== undefined) {
                  setRefAreaLeft(e.activeTooltipIndex);
                }
              }}
              onMouseMove={(e) => {
                if (refAreaLeft !== '' && e && e.activeTooltipIndex !== undefined) {
                  setRefAreaRight(e.activeTooltipIndex);
                }
              }}
              onMouseUp={zoom}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                allowDataOverflow
                dataKey="name"
                domain={[left, right]}
                type="number"
                tickFormatter={(value) => {
                  if (data[value]) {
                    return data[value].time || '';
                  }
                  return '';
                }}
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={['auto', 'auto']}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => {
                  if (magnitude.valueType === 'boolean') {
                    return value === 10 ? 'ON' : 'OFF';
                  }
                  return value.toFixed(magnitude.decimals || 1);
                }}
              />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload[0]) {
                    return (
                      <div style={{
                        background: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '0.5rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <p style={{ margin: 0, fontSize: '0.75rem' }}>
                          {payload[0].payload.fullTime}
                        </p>
                        <p style={{ margin: 0, color: colors[index % colors.length], fontWeight: 'bold' }}>
                          {magnitude.valueType === 'boolean' ? 
                            (payload[0].value === 10 ? 'ON' : 'OFF') : 
                            `${payload[0].value?.toFixed(magnitude.decimals || 1)}${magnitude.unit || ''}`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              
              {magnitude.valueType !== 'boolean' && Number.isFinite(magnitude.minNormalValue) && (
                <ReferenceLine
                  y={magnitude.minNormalValue}
                  stroke="#0284c7"
                  strokeDasharray="3 3"
                  ifOverflow="extendDomain"
                  label={{ value: `Min ${magnitude.minNormalValue}`, position: "left", fontSize: 10 }}
                />
              )}
              {magnitude.valueType !== 'boolean' && Number.isFinite(magnitude.maxNormalValue) && (
                <ReferenceLine
                  y={magnitude.maxNormalValue}
                  stroke="#dc2626"
                  strokeDasharray="3 3"
                  ifOverflow="extendDomain"
                  label={{ value: `Max ${magnitude.maxNormalValue}`, position: "left", fontSize: 10 }}
                />
              )}
              
              <Line 
                type="monotone" 
                dataKey={magnitude.label}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
                animationDuration={300}
              />

              {/* Marcas verticales de comandos ejecutados */}
              {getCommandMarkersForChart().map((commandMarker, cmdIndex) => (
                <ReferenceLine 
                  key={`command-sep-${cmdIndex}`}
                  x={commandMarker.xValue}
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  label={{ 
                    value: commandMarker.commandIcon,
                    position: 'top',
                    offset: 5,
                    style: { 
                      fontSize: '12px', 
                      fill: '#ef4444'
                    }
                  }}
                />
              ))}

              {refAreaLeft && refAreaRight ? (
                <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} fill="#3b82f6" fillOpacity={0.3} />
              ) : null}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ))}
    </div>
  );

  const outerStyle = standalone
    ? { minHeight: '100vh', background: '#f3f4f6', padding: '1rem' }
    : {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      };
  const innerStyle = standalone
    ? { background: 'white', borderRadius: '8px', width: '100%', maxWidth: '1400px', margin: '0 auto', padding: '2rem' }
    : { background: 'white', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', overflow: 'auto', padding: '2rem' };

  return (
    <div style={outerStyle}>
      {/* CSS de impresión: al imprimir, solo se muestra el área de gráficos */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #dh-print-area, #dh-print-area * { visibility: visible !important; }
          #dh-print-area {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 14mm 13mm 16mm 13mm; box-sizing: border-box;
          }
          #dh-print-area .recharts-responsive-container { page-break-inside: avoid; }
          #dh-print-area > div > div { page-break-inside: avoid; }
          .dh-print-only { display: block !important; }
          .dh-no-print { display: none !important; }
          /* margin:0 en la página + padding propio = margen confiable en todos los navegadores */
          @page { size: A4 landscape; margin: 0; }
        }
        .dh-print-only { display: none; }
      `}</style>
      <div style={innerStyle}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Historial del Dispositivo{deviceInfo ? ` - ${deviceInfo.name}` : ''}</h2>
            {data.length > 0 && (
              <div style={{ 
                display: 'inline-block',
                marginLeft: '1rem',
                padding: '0.25rem 0.5rem',
                background: '#fef08a',
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: '#713f12'
              }}>
                📅 {(() => {
                  if (data.length > 0) {
                    const first = new Date(data[0].timestamp);
                    const last = new Date(data[data.length - 1].timestamp);
                    const hours = (last - first) / (1000 * 60 * 60);
                    
                    if (hours < 1) return `${Math.round(hours * 60)} minutos`;
                    if (hours < 24) return `${hours.toFixed(1)} horas`;
                    if (hours < 168) return `${(hours / 24).toFixed(1)} días`;
                    return `${(hours / 168).toFixed(1)} semanas`;
                  }
                  return '';
                })()} | {data.length} puntos
              </div>
            )}
            <p style={{ 
              margin: '0 0 0 1rem', 
              fontSize: '0.75rem', 
              color: '#6b7280' 
            }}>
              Última actualización: {lastUpdateTime.toLocaleString('es-AR', {
                timeZone: 'America/Argentina/Buenos_Aires',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleRefresh} style={{
              padding: '0.5rem 1rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              {loading ? '⏳ Cargando...' : '🔄 Actualizar'}
            </button>
            <button onClick={exportToCSV} style={{
              padding: '0.5rem 1rem',
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              📊 Exportar CSV
            </button>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: autoRefresh ? '#3b82f6' : '#e5e7eb',
              color: autoRefresh ? 'white' : '#6b7280',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Auto 30s
            </label>
            <button onClick={() => window.print()} className="dh-no-print" style={{
              padding: '0.5rem 1rem',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              🖨️ Generar PDF
            </button>
            <button onClick={standalone ? () => window.close() : onClose} style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              {standalone ? 'Cerrar pestaña' : 'Cerrar'}
            </button>
          </div>
        </div>

        {availableMagnitudes.length > 0 && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            background: '#e0f2fe',
            borderRadius: '8px',
            border: '1px solid #0284c7'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.6rem' }}>
                <label style={{ fontWeight: 'bold' }}>
                  📊 Magnitudes a mostrar ({selectedMagnitudes.length}/{availableMagnitudes.length}):
                </label>
                <button
                  onClick={() => setSelectedMagnitudes(availableMagnitudes.map(m => ({ ...m })))}
                  style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', background: '#0284c7', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  ✓ Todas
                </button>
                <button
                  onClick={() => setSelectedMagnitudes([])}
                  style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', background: '#94a3b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  ✗ Ninguna
                </button>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                gap: '0.4rem'
              }}>
                {availableMagnitudes.map((mag, idx) => {
                  const magId = getMagnitudeId(mag);
                  const isChecked = selectedMagnitudes.some(m => getMagnitudeId(m) === magId);
                  const color = colors[idx % colors.length];
                  return (
                    <label
                      key={`magnitude-${idx}-${magId}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.45rem',
                        padding: '0.4rem 0.55rem', borderRadius: '6px', cursor: 'pointer',
                        background: 'white',
                        border: `1px solid ${isChecked ? color : '#e2e8f0'}`,
                        boxShadow: isChecked ? `inset 3px 0 0 ${color}` : 'none',
                        opacity: isChecked ? 1 : 0.6
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleMagnitudeToggle(mag)}
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{ width: 11, height: 11, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{
                        fontSize: '0.82rem',
                        color: isChecked ? '#1e293b' : '#64748b',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        {mag.label}{mag.unit ? ` (${mag.unit})` : ''}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            
            {availableMagnitudes.length > 1 && (
              <div>
                <label style={{ fontWeight: 'bold', marginRight: '1rem' }}>
                  📈 Modo de visualización:
                </label>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '2px solid #0284c7',
                    borderRadius: '4px',
                    background: 'white',
                    fontSize: '1rem'
                  }}
                >
                  <option value="combined">Gráfico Combinado</option>
                  <option value="separate">Gráficos Separados</option>
                </select>
              </div>
            )}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          alignItems: 'center',
          background: '#f3f4f6',
          padding: '1rem',
          borderRadius: '8px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontWeight: 'bold' }}>Desde:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '2px solid #3b82f6',
                borderRadius: '4px'
              }}
            />
            <label style={{ fontWeight: 'bold' }}>Hasta:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '2px solid #3b82f6',
                borderRadius: '4px'
              }}
            />
          </div>

          
          <button
            onClick={handleDateChange}
            style={{
              padding: '0.5rem 1rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🔄 Aplicar Fechas
          </button>
          
          {isZoomed && (
            <button
              onClick={zoomOut}
              style={{
                padding: '0.5rem 1rem',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🔍 Resetear Zoom
            </button>
          )}
        </div>

        {viewMode === 'combined' && Object.keys(stats).length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${selectedMagnitudes.length}, 1fr)`,
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {selectedMagnitudes.map((magnitude, idx) => {
              const magStats = stats[magnitude.label];
              if (!magStats) return null;
              
              return (
                <div key={magnitude.name} style={{ 
                  background: '#f9fafb', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  borderLeft: `4px solid ${colors[idx % colors.length]}`
                }}>
                  <div style={{ fontWeight: 'bold', color: colors[idx % colors.length], marginBottom: '0.5rem' }}>
                    {magnitude.label}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Promedio: </span>
                      <strong>{magStats.avg}{magStats.unit}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Mín/Máx: </span>
                      <strong>{magStats.min}/{magStats.max}{magStats.unit}</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div id="dh-print-area">
          <div className="dh-print-only" style={{ marginBottom: '1.1rem', paddingBottom: '0.7rem', borderBottom: '3px solid #0c4a6e' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#0284c7', letterSpacing: '0.5px' }}>NUBEMOTIC · Telemetría 24/7</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#0c4a6e', marginTop: '0.1rem' }}>
                  Historial del Dispositivo
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                  {deviceInfo ? deviceInfo.name : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#475569', lineHeight: 1.6 }}>
                <div><b>Período:</b> {startDate} — {endDate}</div>
                <div>{data.length} registros · {viewMode === 'combined' ? 'Gráfico combinado' : 'Gráficos separados'}</div>
                {isZoomed ? <div style={{ color: '#b91c1c', fontWeight: 600 }}>🔍 Vista con zoom aplicado</div> : null}
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Emitido: {new Date().toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.5rem' }}>
              <b>Magnitudes visualizadas:</b> {selectedMagnitudes.length > 0 ? selectedMagnitudes.map(m => `${m.label}${m.unit ? ` (${m.unit})` : ''}`).join('  ·  ') : '—'}
            </div>
          </div>
          {selectedMagnitudes.length === 0 ? (
            <div style={{
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              background: '#f9fafb',
              borderRadius: '8px',
              color: '#64748b'
            }}>
              <div style={{ fontSize: '2rem' }}>📊</div>
              <p style={{ margin: 0, fontWeight: 600 }}>Seleccioná una o más magnitudes arriba para ver los gráficos</p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Usá los checkboxes o el botón "✓ Todas".</p>
            </div>
          ) : data.length > 0 ? (
            viewMode === 'combined' ? renderCombinedChart() : renderSeparateCharts()
          ) : (
            <div style={{
              height: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f9fafb',
              borderRadius: '8px'
            }}>
              <p>No hay datos para mostrar en el rango seleccionado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceHistory;